import {MeasurementPref} from "./MeasurementPref";

export enum HikeTime {
  twoKmPerHourAnd300MetersPerHour = 'twoKmPerHourAnd300MetersPerHour',
  threeKmPerHourAnd400MetersPerHour = 'threeKmPerHourAnd400MetersPerHour',
  fourKmPerHourAnd500MetersPerHour = 'fourKmPerHourAnd500MetersPerHour',
  fiveKmPerHourAnd600MetersPerHour = 'fiveKmPerHourAnd600MetersPerHour',
  sixKmPerHourAnd700MetersPerHour = 'sixKmPerHourAnd700MetersPerHour',
  sevenKmPerHourAnd800MetersPerHour = 'sevenKmPerHourAnd800MetersPerHour',
  eightKmPerHourAnd900MetersPerHour = 'eightKmPerHourAnd900MetersPerHour',
  nineKmPerHourAnd1000MetersPerHour = 'nineKmPerHourAnd1000MetersPerHour',
  tenKmPerHourAnd1100MetersPerHour = 'tenKmPerHourAnd1100MetersPerHour',
}

export enum HikeTimeDistance {
  twoKmPerHour = 'twoKmPerHour',
  threeKmPerHour = 'threeKmPerHour',
  fourKmPerHour = 'fourKmPerHour',
  fiveKmPerHour = 'fiveKmPerHour',
  sixKmPerHour = 'sixKmPerHour',
  sevenKmPerHour = 'sevenKmPerHour',
  eightKmPerHour = 'eightKmPerHour',
  nineKmPerHour = 'nineKmPerHour',
  tenKmPerHour = 'tenKmPerHour',
}

export enum HikeTimeElevation {
  threeHundredMetersPerHour = 'threeHundredMetersPerHour',
  fourHundredMetersPerHour = 'fourHundredMetersPerHour',
  fiveHundredMetersPerHour = 'fiveHundredMetersPerHour',
  sixHundredMetersPerHour = 'sixHundredMetersPerHour',
  sevenHundredMetersPerHour = 'sevenHundredMetersPerHour',
  eightHundredMetersPerHour = 'eightHundredMetersPerHour',
  nineHundredMetersPerHour = 'nineHundredMetersPerHour',
  oneThousandMetersPerHour = 'oneThousandMetersPerHour',
  oneThousandOneHundredMetersPerHour = 'oneThousandOneHundredMetersPerHour',
}

const DISTANCE_KM_PER_HOUR: Record<string, number> = {
  twoKmPerHour: 2,
  threeKmPerHour: 3,
  fourKmPerHour: 4,
  fiveKmPerHour: 5,
  sixKmPerHour: 6,
  sevenKmPerHour: 7,
  eightKmPerHour: 8,
  nineKmPerHour: 9,
  tenKmPerHour: 10,
};

const ELEVATION_METERS_PER_HOUR: Record<string, number> = {
  threeHundredMetersPerHour: 300,
  fourHundredMetersPerHour: 400,
  fiveHundredMetersPerHour: 500,
  sixHundredMetersPerHour: 600,
  sevenHundredMetersPerHour: 700,
  eightHundredMetersPerHour: 800,
  nineHundredMetersPerHour: 900,
  oneThousandMetersPerHour: 1000,
  oneThousandOneHundredMetersPerHour: 1100,
};

const LEGACY_TO_PAIR: Record<string, [string, string]> = {
  twoKmPerHourAnd300MetersPerHour: ['twoKmPerHour', 'threeHundredMetersPerHour'],
  threeKmPerHourAnd400MetersPerHour: ['threeKmPerHour', 'fourHundredMetersPerHour'],
  fourKmPerHourAnd500MetersPerHour: ['fourKmPerHour', 'fiveHundredMetersPerHour'],
  fiveKmPerHourAnd600MetersPerHour: ['fiveKmPerHour', 'sixHundredMetersPerHour'],
  sixKmPerHourAnd700MetersPerHour: ['sixKmPerHour', 'sevenHundredMetersPerHour'],
  sevenKmPerHourAnd800MetersPerHour: ['sevenKmPerHour', 'eightHundredMetersPerHour'],
  eightKmPerHourAnd900MetersPerHour: ['eightKmPerHour', 'nineHundredMetersPerHour'],
  nineKmPerHourAnd1000MetersPerHour: ['nineKmPerHour', 'oneThousandMetersPerHour'],
  tenKmPerHourAnd1100MetersPerHour: ['tenKmPerHour', 'oneThousandOneHundredMetersPerHour'],
};

export class HikeTimeUtility {
  private static baseDistanceMultiplier = 0.012;
  private static baseElevGainMultiplier = 0.10;

  hikeTime: HikeTime;
  hikeTimeDistance: string;
  hikeTimeElevation: string;

  constructor(hikeTime: HikeTime, distance?: string, elevation?: string) {
    this.hikeTime = hikeTime;
    const pair = LEGACY_TO_PAIR[hikeTime] || ['fiveKmPerHour', 'sixHundredMetersPerHour'];
    this.hikeTimeDistance = distance ?? pair[0];
    this.hikeTimeElevation = elevation ?? pair[1];
  }

  static fromPair(distance: string, elevation: string): HikeTimeUtility {
    // Synthesise the closest legacy enum for backwards compat with callers
    // that still inspect `.hikeTime` directly.
    const legacyKey = Object.keys(LEGACY_TO_PAIR).find(k => {
      const p = LEGACY_TO_PAIR[k];
      return p[0] === distance && p[1] === elevation;
    }) || 'fiveKmPerHourAnd600MetersPerHour';
    return new HikeTimeUtility(legacyKey as HikeTime, distance, elevation);
  }

  kmDistance(): number {
    return DISTANCE_KM_PER_HOUR[this.hikeTimeDistance] ?? 5;
  }

  meterElevationGain(): number {
    return ELEVATION_METERS_PER_HOUR[this.hikeTimeElevation] ?? 600;
  }

  distanceMultiplier(): number {
    return HikeTimeUtility.baseDistanceMultiplier * (5.0 / this.kmDistance());
  }

  elevGainMultiplier(): number {
    return HikeTimeUtility.baseElevGainMultiplier * (600.0 / this.meterElevationGain());
  }

  multiplier(): number {
    switch (this.hikeTime) {
      case HikeTime.twoKmPerHourAnd300MetersPerHour:
        return 1.6;
      case HikeTime.threeKmPerHourAnd400MetersPerHour:
        return 1.4;
      case HikeTime.fourKmPerHourAnd500MetersPerHour:
        return 1.2;
      case HikeTime.fiveKmPerHourAnd600MetersPerHour:
        return 1.0;
      case HikeTime.sixKmPerHourAnd700MetersPerHour:
        return 0.9;
      case HikeTime.sevenKmPerHourAnd800MetersPerHour:
        return 0.8;
      case HikeTime.eightKmPerHourAnd900MetersPerHour:
        return 0.7;
      case HikeTime.nineKmPerHourAnd1000MetersPerHour:
        return 0.6;
      case HikeTime.tenKmPerHourAnd1100MetersPerHour:
        return 0.5;
    }
  }

  prettyString(): string {
    return `${this.kmDistance()} km/h and ${this.meterElevationGain()} m/h`;
  }

  static preferenceFromString(preference: string): HikeTimeUtility {
    switch (preference) {
      case '2 km/h and 300 m/h':
        return new HikeTimeUtility(HikeTime.twoKmPerHourAnd300MetersPerHour);
      case '3 km/h and 400 m/h':
        return new HikeTimeUtility(HikeTime.threeKmPerHourAnd400MetersPerHour);
      case '4 km/h and 500 m/h':
        return new HikeTimeUtility(HikeTime.fourKmPerHourAnd500MetersPerHour);
      case '5 km/h and 600 m/h':
        return new HikeTimeUtility(HikeTime.fiveKmPerHourAnd600MetersPerHour);
      case '6 km/h and 700 m/h':
        return new HikeTimeUtility(HikeTime.sixKmPerHourAnd700MetersPerHour);
      case '7 km/h and 800 m/h':
        return new HikeTimeUtility(HikeTime.sevenKmPerHourAnd800MetersPerHour);
      case '8 km/h and 900 m/h':
        return new HikeTimeUtility(HikeTime.eightKmPerHourAnd900MetersPerHour);
      case '9 km/h and 1000 m/h':
        return new HikeTimeUtility(HikeTime.nineKmPerHourAnd1000MetersPerHour);
      case '10 km/h and 1100 m/h':
        return new HikeTimeUtility(HikeTime.tenKmPerHourAnd1100MetersPerHour);
    }
    switch (preference.toLowerCase()) {
      case 'twoKmPerHourAnd300MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.twoKmPerHourAnd300MetersPerHour);
      case 'threeKmPerHourAnd400MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.threeKmPerHourAnd400MetersPerHour);
      case 'fourKmPerHourAnd500MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.fourKmPerHourAnd500MetersPerHour);
      case 'fiveKmPerHourAnd600MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.fiveKmPerHourAnd600MetersPerHour);
      case 'sixKmPerHourAnd700MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.sixKmPerHourAnd700MetersPerHour);
      case 'sevenKmPerHourAnd800MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.sevenKmPerHourAnd800MetersPerHour);
      case 'eightKmPerHourAnd900MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.eightKmPerHourAnd900MetersPerHour);
      case 'nineKmPerHourAnd1000MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.nineKmPerHourAnd1000MetersPerHour);
      case 'tenKmPerHourAnd1100MetersPerHour'.toLowerCase():
        return new HikeTimeUtility(HikeTime.tenKmPerHourAnd1100MetersPerHour);
    }
    throw new Error(`Unknown preference: ${preference}`);
  }

  secondsDurationForWalkDistance(distanceMeters: number): number {
    const mins = distanceMeters * this.distanceMultiplier()
    return mins * 60;
  }

  secondsDurationForElevationGain(elevationMeters: number): number {
    const mins = elevationMeters * this.elevGainMultiplier()
    return mins * 60;
  }

  secondsDuration(
    elevationGainMeters: number,
    distanceMeters: number
  ): number {
    return (
      this.secondsDurationForWalkDistance(distanceMeters) +
      this.secondsDurationForElevationGain(elevationGainMeters)
    );
  }


  static preference(): HikeTimeUtility {
    if (window.user) {
      // @ts-ignore
      const distance = window.user.hike_time_distance as string | undefined;
      // @ts-ignore
      const elevation = window.user.hike_time_elevation as string | undefined;
      if (distance && elevation) {
        return HikeTimeUtility.fromPair(distance, elevation);
      }
      // @ts-ignore
      return HikeTimeUtility.preferenceFromString(window.user.hike_time.toLowerCase())
    } else {
      return new HikeTimeUtility(HikeTime.fiveKmPerHourAnd600MetersPerHour)
    }
  }
}

export class PrettyTime {
  private hoursPerDay: number = 5.0;

  constructor(private timeInterval: number) {}

  prettyTime(): string {
    // Never show days; always present as hours/minutes/seconds
    const totalSeconds = Math.floor(this.timeInterval);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (hours > 0) {
      parts.push(`${hours} hrs`);
      if (minutes > 0) parts.push(`${minutes} min`);
    } else if (minutes > 0) {
      parts.push(`${minutes} min`);
    } else {
      parts.push(`${seconds} sec`);
    }

    return parts.join(' ');
  }

  unadjustedPrettyTime(): string {
    // Also avoid days here; present as hours/minutes/seconds
    const totalSeconds = Math.floor(this.timeInterval);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (hours > 0) {
      parts.push(`${hours} hrs`);
      if (minutes > 0) parts.push(`${minutes} min`);
    } else if (minutes > 0) {
      parts.push(`${minutes} min`);
    } else {
      parts.push(`${seconds} sec`);
    }

    return parts.join(' ');
  }
}
